// SPDX-License-Identifier: AGPL-3.0-only

/**
 * Authentication state.
 *
 * # WHAT "AUTHENTICATED" MEANS HERE, AND WHAT IT DOES NOT
 *
 * This tracks whether the browser currently has a working session. It does NOT
 * track permissions, and there is deliberately no `can()` helper on it.
 *
 * The reason mirrors the server's: authentication proves who you are,
 * authorization proves what you may do, and a client that decides the second
 * from the first will eventually be believed. Every authorization decision is
 * the server's, on every request, against grants read from PostgreSQL. The UI
 * may hide a button it expects to fail, but hiding is a courtesy — the server
 * refuses regardless, and a hidden button is not a control.
 *
 * # HOW THE SESSION IS DISCOVERED ON A RELOAD
 *
 * The session cookie is `HttpOnly`, so this code cannot read it. Whether a
 * session exists is therefore established by ASKING THE SERVER: a cheap
 * authenticated request that succeeds or returns 401. That is the only honest
 * way — any client-side flag would be a guess that survives logout.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError, api, setCsrfToken } from '../api/client';
import type { Organization } from '../api/client';

interface AuthState {
  /** Undefined while the initial check is in flight. */
  organization: Organization | null | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [organization, setOrganization] = useState<Organization | null | undefined>(undefined);

  /**
   * Ask the server whether this browser has a session.
   *
   * A 401 is the ordinary answer for "not logged in" and is not an error worth
   * surfacing. Anything else IS surfaced, because "the server is unreachable"
   * and "you are logged out" must not look the same to a user — the server
   * makes that distinction too (503 versus 401), and flattening it here would
   * throw it away.
   */
  const refresh = useCallback(async () => {
    try {
      // The session first: it carries the CSRF token, which lives only in
      // memory and is therefore lost on every reload. Recovering it here is what
      // lets a refreshed page still submit a form.
      const session = await api.session();
      setCsrfToken(session.csrf_token);
      setOrganization(await api.organization());
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setOrganization(null);
        return;
      }
      // Unreachable or degraded: not authenticated, but not a clean logout
      // either. Left null so the app shows the login page, and the error is
      // reported by whatever triggered the call.
      setOrganization(null);
      throw err;
    }
  }, []);

  useEffect(() => {
    // A failed initial check must not crash the app; the login page is the
    // correct destination either way.
    void refresh().catch(() => undefined);
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await api.login(email, password);
      // The CSRF token is returned in the BODY and held in memory. It must be
      // echoed in a header, which a cross-site page cannot set.
      setCsrfToken(result.csrf_token);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      // The local state is cleared even if the request failed. A UI that stayed
      // "logged in" because logout errored would be lying about a security
      // action the user believes they performed; the server-side session may
      // survive, but the browser stops presenting itself as signed in and the
      // next request will discover the truth.
      setCsrfToken(null);
      setOrganization(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      organization,
      isAuthenticated: organization != null,
      isLoading: organization === undefined,
      login,
      logout,
    }),
    [organization, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside an AuthProvider');
  }
  return ctx;
}
